// src/services/openaiService.js - Fixed Version
class OpenAIService {
    constructor() {
      this.apiKey = process.env.REACT_APP_OPENAI_API_KEY;
      this.apiUrl = 'https://api.openai.com/v1/chat/completions';    
    }
  
    async analyzeTask(task) {
      try {
        // if (!this.apiKey) {
        //   console.log(' No OpenAI key, using fallback analysis');
        //   return this.fallbackAnalysis(task);
        // }
  
        // console.log(' Analyzing task with real OpenAI:', task.title);
  
        const response = await fetch(this.apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.apiKey}`
          },
          body: JSON.stringify({
            model: 'gpt-3.5-turbo',
            messages: [
              {
                role: 'system',
                content: this.getSystemPrompt()
              },
              {
                role: 'user',
                content: this.buildTaskPrompt(task)
              }
            ],
            temperature: 0.3,
            max_tokens: 500
          })
        });
  
        if (!response.ok) {
          const errorData = await response.json();
          console.error('❌ OpenAI API error:', response.status, errorData);
          
          if (response.status === 401) {
            throw new Error('Invalid OpenAI API key');
          } else if (response.status === 429) {
            throw new Error('OpenAI rate limit exceeded');
          } else if (response.status === 402) {
            throw new Error('OpenAI quota exceeded - add billing');
          }
          
          throw new Error(`OpenAI API error: ${response.status}`);
        }
  
        const data = await response.json();
        const result = this.parseOpenAIResponse(data.choices[0].message.content, task);
        
        console.log('✅ OpenAI analysis complete:', result);
        return result;
  
      } catch (error) {
        console.error('❌ OpenAI error:', error.message);
        
        // Always fall back to mock analysis on error
        console.log('🔄 Falling back to mock analysis');
        return this.fallbackAnalysis(task);
      }
    }
  
    getSystemPrompt() {
      return `You are an AI assistant that analyzes tasks and assigns priority levels. Analyze each task based on urgency, importance, and time sensitivity.
  
  Priority levels:
  - HIGH (8-10): Urgent, time-sensitive, or very important tasks
  - MEDIUM (4-7): Moderately important tasks  
  - LOW (1-3): Less urgent or optional tasks
  
  Respond only with valid JSON in the exact format requested.`;
    }
  
    buildTaskPrompt(task) {
      return `Analyze this task:
  
  Title: "${task.title}"
  Description: "${task.description || 'No description provided'}"
  
  Please respond with this JSON format only:
  {
    "priority": <number 1-10>,
    "priorityLevel": "<low|medium|high>",
    "reasoning": "<explain your priority decision>",
    "confidence": <number 0-1>,
    "keywords": ["<relevant keywords>"]
  }`;
    }
  
    parseOpenAIResponse(content, task) {
      try {
        // Try to extract JSON from the response
        const jsonMatch = content.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0]);
          
          // Validate and clean the response
          return {
            priority: Math.max(1, Math.min(10, Math.round(parsed.priority || 5))),
            priorityLevel: this.validatePriorityLevel(parsed.priorityLevel),
            reasoning: (parsed.reasoning || 'AI analysis completed').substring(0, 200),
            confidence: Math.max(0, Math.min(1, parsed.confidence || 0.7)),
            keywords: Array.isArray(parsed.keywords) 
              ? parsed.keywords.slice(0, 5).map(k => String(k)) 
              : []
          };
        }
      } catch (error) {
        console.error(' Failed to parse OpenAI response:', error);
      }
      
      // If parsing fails, return fallback
      return this.fallbackAnalysis(task);
    }
  
    validatePriorityLevel(level) {
      const validLevels = ['low', 'medium', 'high'];
      return validLevels.includes(level) ? level : 'medium';
    }
  
    // Simplified fallback analysis (more like natural OpenAI reasoning)
    fallbackAnalysis(task) {
      const text = ((task.title || '') + ' ' + (task.description || '')).toLowerCase();
      
      const urgentKeywords = [
        'urgent', 'asap', 'emergency', 'critical', 'deadline', 'now', 'today', 'tomorrow',
        'pick up', 'pickup', 'appointment', 'meeting', 'after', 'before', 'by'
      ];
      
      const timeIndicators = [
        'am', 'pm', 'o\'clock', 'morning', 'afternoon', 'evening', 'night',
        'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'
      ];
      
      const lowKeywords = [
        'someday', 'maybe', 'eventually', 'when possible', 'optional', 
        'organize', 'clean', 'sort', 'hobby', 'leisure'
      ];
  
      let priority = 5;
      let foundKeywords = [];
      let reasoning = 'Medium priority task';
  
      // Check for urgent or time-sensitive indicators
      const urgentFound = urgentKeywords.filter(keyword => {
        if (text.includes(keyword)) {
          foundKeywords.push(keyword);
          return true;
        }
        return false;
      });
  
      const timeFound = timeIndicators.filter(indicator => {
        if (text.includes(indicator)) {
          foundKeywords.push(indicator);
          return true;
        }
        return false;
      });
  
      const lowFound = lowKeywords.filter(keyword => {
        if (text.includes(keyword)) {
          foundKeywords.push(keyword);
          return true;
        }
        return false;
      });
  
      // Simple priority logic (more like natural AI reasoning)
      if (urgentFound.length > 0 || timeFound.length > 0) {
        priority = 8;
        reasoning = `High priority due to time sensitivity and urgency indicators`;
      } else if (lowFound.length > 0) {
        priority = 3;
        reasoning = `Low priority - appears to be optional or non-urgent`;
      } else {
        priority = 5;
        reasoning = `Medium priority - standard importance level`;
      }
  
      const priorityLevel = priority >= 7 ? 'high' : priority >= 4 ? 'medium' : 'low';
  
      return {
        priority,
        priorityLevel,
        reasoning: reasoning + ' (fallback analysis)',
        confidence: 0.6,
        keywords: foundKeywords.slice(0, 5)
      };
    }
  
    estimateTime(task) {
      const text = ((task.title || '') + ' ' + (task.description || '')).toLowerCase();
      let baseTime = 60; // 1 hour default
  
      // Task type adjustments
      if (text.includes('meeting')) baseTime = 60;
      if (text.includes('call')) baseTime = 30;
      if (text.includes('email')) baseTime = 15;
      if (text.includes('review')) baseTime = 45;
      if (text.includes('write') || text.includes('create')) baseTime = 90;
      if (text.includes('research')) baseTime = 120;
      if (text.includes('project')) baseTime = 180;
      if (text.includes('presentation')) baseTime = 120;
      if (text.includes('analysis') || text.includes('analyze')) baseTime = 90;
  
      // Complexity adjustments
      if (text.includes('complex') || text.includes('detailed')) baseTime *= 1.5;
      if (text.includes('simple') || text.includes('quick') || text.includes('brief')) baseTime *= 0.5;
      if (text.includes('comprehensive') || text.includes('thorough')) baseTime *= 2;
  
      // Effort level adjustments
      if (text.includes('draft') || text.includes('outline')) baseTime *= 0.7;
      if (text.includes('final') || text.includes('complete')) baseTime *= 1.3;
  
      return Math.max(5, Math.min(480, Math.round(baseTime)));
    }
  
  }
  
  export const openaiService = new OpenAIService();